import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import EntityUsersPage from '@features/user/pages/EntityUsersPage';
import EntityScopePicker, {
  EntityScopePickerChange,
} from '@features/entity/components/EntityScopePicker';
import { useEntityContext } from '@features/entity/hooks/usePermissions';
import {
  ENTITY_SCOPE_TOGGLE_CLASS,
  encodeEntityScopeDestination,
  formatEntityScopeLabel,
} from '@features/entity/constants/entityScopePicker';

interface ActiveScope {
  scope: string;
  scopeRefId: number;
  name?: string;
}

const DistrictUsersPage: React.FC = () => {
  const { districtId } = useParams<{ districtId: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const sid = Number(districtId);
  const { context } = useEntityContext();

  const ownLabel = useMemo(() => {
    const name = context?.scopeName?.trim() || 'This District';
    return formatEntityScopeLabel('DISTRICT', name.replace(/^District:\s*/i, ''));
  }, [context?.scopeName]);

  const [active, setActive] = useState<ActiveScope>({
    scope: 'DISTRICT',
    scopeRefId: sid,
    name: ownLabel,
  });

  useEffect(() => {
    if (!(sid > 0)) return;
    setActive((prev) =>
      prev.scope === 'DISTRICT' && prev.scopeRefId !== sid
        ? { scope: 'DISTRICT', scopeRefId: sid, name: ownLabel }
        : prev.scope === 'DISTRICT' && !prev.name
          ? { ...prev, name: ownLabel }
          : prev
    );
  }, [sid, ownLabel]);

  const destinationValue = useMemo(
    () => encodeEntityScopeDestination(active.scope, active.scopeRefId),
    [active.scope, active.scopeRefId]
  );

  const handleScopeChange = (next: EntityScopePickerChange) => {
    // Clear list URL state so remounted useSearch does not inherit filters from the prior entity.
    if (searchParams.toString()) {
      setSearchParams({}, { replace: true });
    }
    setActive({
      scope: next.scope,
      scopeRefId: next.scopeRefId,
      name: next.name,
    });
  };

  const scopeSelector =
    sid > 0 ? (
      <EntityScopePicker
        ownScopeValue="D"
        ownScopeRefId={sid}
        ownLabel={ownLabel}
        value={destinationValue}
        onChange={handleScopeChange}
        readOnlyWhenNoChildren={false}
        toggleClassName={ENTITY_SCOPE_TOGGLE_CLASS}
        testId="district-users-scope-picker"
      />
    ) : null;

  const listScope = (active.scope.toUpperCase() || 'DISTRICT') as
    | 'NATION'
    | 'DISTRICT'
    | 'CHURCH';

  return (
    <div key={`${active.scope}:${active.scopeRefId}`}>
      <EntityUsersPage
        scope={listScope}
        scopeRefId={active.scopeRefId}
        title="Users"
        subtitle="Manage people with roles at this district and its churches"
        leadingControl={scopeSelector}
        testId="district-users-search"
      />
    </div>
  );
};

export default DistrictUsersPage;
